/**
 * Dropzone para carga de archivos.
 *
 * Pendiente: este componente asume que `arrExistsFiles` siempre existe.
 * En `onDrop` se usa `arrExistsFiles!` y se concatena con `arrAcceptedFiles`, por lo que si
 * `arrExistsFiles` no se pasa, el componente lanzara en runtime.
 */
import { Fragment, useCallback, useMemo, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useDropzone } from 'react-dropzone'
import { FileDropZone } from './fileDropZone.type'

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px 35px',
  borderWidth: 2,
  borderRadius: 20,
  borderColor: '#DDDDDD',
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
}

const acceptStyle = {
  borderColor: '#101E8E',
}

const rejectStyle = {
  borderColor: 'red',
}

const faFileImage = 'fa-file-image'
const faFilePdf = 'fa-file-pdf'
const faFileCsv = 'fa-file-csv'
const faFileExcel = 'fa-file-excel'
const faFileWord = 'fa-file-word'
const faFilePowerPoint = 'fa-file-powerpoint'

export const MAPA_ICON = new Map<string, string>([
  ['jpeg', faFileImage],
  ['png', faFileImage],
  ['jpg', faFileImage],
  ['pdf', faFilePdf],
  ['csv', faFileCsv],
  ['xls', faFileExcel],
  ['xlsx', faFileExcel],
  ['doc', faFileWord],
  ['docx', faFileWord],
  ['ppt', faFilePowerPoint],
  ['pptx', faFilePowerPoint],
])

type TProps = {
  arrAcceptedFiles: FileDropZone[]
  setArrAcceptedFiles: (value: any) => void
  arrExistsFiles?: FileDropZone[]
  fnDeleteExistFile: (id: string) => void
  height?: number
  width?: string
  heightMaxScroll?: number
  textTypeFile?: string
  isVisuallyHidden?: boolean
  creationMode?: boolean
  savedFiles?: boolean
}

export const FormDropZone = ({
  arrAcceptedFiles,
  setArrAcceptedFiles,
  arrExistsFiles,
  fnDeleteExistFile,
  height,
  width,
  heightMaxScroll,
  textTypeFile = 'Todavía no hay reportes de cumplimiento',
  isVisuallyHidden,
  creationMode,
  savedFiles,
}: TProps) => {
  const [arrRepeatedFiles, setArrRepeatedFiles] = useState([] as any[])
  const [showListRepeatedFiles, setShowListRepeatedFiles] = useState(false)

  const onDrop = useCallback(
    (acceptedFiles) => {
      setArrRepeatedFiles([])
      const repeated: FileDropZone[] = []
      const notRepeated: FileDropZone[] = []

      if (acceptedFiles) {
        acceptedFiles.forEach((af: any) => {
          const a = [...arrExistsFiles!, ...arrAcceptedFiles]
          if (a.some((f: any) => f.name === af.path)) {
            repeated.push({
              file: af,
              isNew: true,
              name: af.path,
              size: af.size,
            })
          } else {
            notRepeated.push({
              file: af,
              isNew: true,
              name: af.path,
              size: af.size,
              ext: af.path.split('.').pop(),
            })
          }
        })

        if (notRepeated && notRepeated.length > 0) {
          setArrAcceptedFiles((current: FileDropZone[]) => [...current, ...notRepeated])
        }

        if (repeated && repeated.length > 0) {
          setArrRepeatedFiles((current) => [...current, ...repeated])
          setShowListRepeatedFiles(true)
        }
      }
    },
    [arrAcceptedFiles, arrExistsFiles]
  )

  const { getRootProps, isDragAccept, isDragReject, open, getInputProps } = useDropzone({ onDrop })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...{
        maxheight: height ? height : 350,
        maxWidth: width ? width : '100%',
        height: 300,
      },
    }),
    [isDragAccept, isDragReject]
  )

  /**
   * @description Elimina archivo de listado de archivos (nuevos o existentes)
   *
   * @author luis.durazo, Hábil MX
   * @version 1.0.0, 09/01/2023
   * @param index Indice del archivo a eliminar
   * @param isNewFile Indicador para determinar si es un archivo nuevo o existente
   * @param id Identificador del archivo (solo para archivos existentes)
   */
  const deleteFileSelected = (index: number, isNewFile: boolean, id?: string) => {
    if (isNewFile) {
      setArrAcceptedFiles((ov: any) => {
        return ov.filter((_: any, i: number) => i !== index)
      })
    } else if (id) {
      fnDeleteExistFile(id)
    }
  }

  /**
   * @description Genera listado de archivos (existentes y nuevos)
   *
   * @author luis.durazo, Hábil MX
   * @version 1.0.0, 09/01/2023
   * @param files Arreglo de archivos
   * @param isNewFiles Indicador para determinar si el arreglo de archivos son nuevos
   * @returns Fragment con listado archivos
   */
  const generateFileList = (files: FileDropZone[], isNewFiles: boolean) => {
    return files?.map((fileDropZone: FileDropZone, index) => (
      <Fragment key={index}>
        <div className='d-flex align-items-center mb-7 justify-content-between'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-50px me-5'>
              <span
                className={`symbol-label ${isNewFiles ? 'bg-light-primary' : 'bg-light-success'}`}>
                <i
                  className={`fa-solid fs-1 m-x1 ${
                    fileDropZone.ext && MAPA_ICON.get(fileDropZone.ext)
                      ? MAPA_ICON.get(fileDropZone.ext)
                      : 'fa-file'
                  } ${isNewFiles ? 'text-primary' : 'text-success'}`}></i>
              </span>
            </div>
            <div className='d-flex flex-column text-break'>
              <span className={`text-dark text-hover-primary fs-6 fw-bold `}>
                {fileDropZone.name}
              </span>
              {isNewFiles && (
                <span className='text-muted fw-semibold'>{fileDropZone.size} bytes</span>
              )}
            </div>
          </div>
          <div className='flex-grow-1 ps-3 d-flex justify-content-end'>
            {!fileDropZone.isNew && (
              <OverlayTrigger placement={'top'} overlay={<Tooltip>Ver archivo</Tooltip>}>
                <a
                  className='btn btn-outline-danger btn-icon btn-circle'
                  href={fileDropZone.url}
                  target='_blank'
                  rel='noopener noreferrer'>
                  <i className='fa-solid fa-arrow-up-right-from-square fs-3'></i>
                </a>
              </OverlayTrigger>
            )}
            {creationMode && (
              <OverlayTrigger placement={'top'} overlay={<Tooltip>Quitar archivo</Tooltip>}>
                <button
                  className={`btn btn-outline-danger btn-icon btn-circle ${
                    isVisuallyHidden ? 'visually-hidden' : ''
                  }`}
                  type='button'
                  data-bs-toggle='tooltip'
                  data-bs-placement='left'
                  title='Agregar contacto'
                  onClick={() => deleteFileSelected(index, isNewFiles, fileDropZone!.id)}>
                  <i className='fa-solid fa-minus fs-3'></i>
                </button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </Fragment>
    ))
  }

  const acceptedFiles = generateFileList(arrAcceptedFiles, true)
  const existsFiles = generateFileList(arrExistsFiles!, false)
  return (
    <section>
      <div className='row'>
        {!isVisuallyHidden ? (
          <>
            {creationMode && (
              <div className='col'>
                <div
                  {...getRootProps({ style })}
                  className='hover-scroll-overlay-y  me-n2 '
                  onClick={open}>
                  <input {...getInputProps()} />
                  <div className='d-flex flex-column align-items-center  justify-content-end w-100 h-100 '>
                    <div className='mt-auto'>
                      <div className='d-flex flex-column align-items-center mt-0'>
                        <i className='fa-solid fa-upload fs-3x mb-5'></i>
                        <h4 className='text-gray-600 text-center'>
                          Suelta aquí los archivos o selecciónalos
                        </h4>
                      </div>
                    </div>
                    <div className='mt-auto'>
                      <div className='w-100 d-flex justify-content-center'>
                        <div className='d-flex align-items-center mt-4'>
                          <div className='symbol symbol-30px'>
                            <span className='symbol-label bg-light-success'>
                              <i className='fa-solid fs-3 text-success fa-file'></i>
                            </span>
                          </div>
                          <div>
                            <span className='ms-2 text-muted fs-6 fw-bold'>Archivo cargado</span>
                          </div>
                        </div>
                        <div className='d-flex align-items-center mt-4 ms-4'>
                          <div className='symbol symbol-30px'>
                            <span className='symbol-label bg-light-primary'>
                              <i className='fa-solid fs-3 text-primary fa-file'></i>
                            </span>
                          </div>
                          <div>
                            <span className='ms-2 text-muted fs-6 fw-bold'>
                              Archivo pendiente de cargar
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : null}

        {savedFiles && (
          <div className='col'>
            {showListRepeatedFiles && arrRepeatedFiles && arrRepeatedFiles.length > 0 && (
              <div className='alert alert-dismissible bg-light-warning d-flex p-5 mb-1 align-items-start'>
                <i className='fa-solid fa-triangle-exclamation me-4 mb-5 mb-sm-0 fs-2qx text-warning'></i>
                <div className='d-flex flex-column pe-1'>
                  <h5 className='fw-normal mb-2'>
                    Los siguientes archivos se encuentran repetidos y no serán cargados:
                  </h5>
                  <ul
                    className='mb-0 hover-scroll-overlay-y me-n2 px-2 '
                    style={{ maxHeight: '50px' }}>
                    {arrRepeatedFiles.map((r, index) => (
                      <Fragment key={index}>
                        <li>{r.name}</li>
                      </Fragment>
                    ))}
                  </ul>
                </div>
                <button
                  type='button'
                  className='position-absolute position-sm-relative m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                  onClick={() => setShowListRepeatedFiles(false)}>
                  <i className='fa-solid fa-xmark'></i>
                </button>
              </div>
            )}
            {(arrExistsFiles && arrExistsFiles.length > 0) ||
            (arrAcceptedFiles && arrAcceptedFiles.length > 0) ? (
              <div
                className={`hover-scroll-overlay-y me-n2  pt-7 hide-scroll-x`}
                style={{
                  maxHeight: `${
                    heightMaxScroll && showListRepeatedFiles
                      ? heightMaxScroll - 130
                      : heightMaxScroll && !showListRepeatedFiles
                      ? heightMaxScroll
                      : 350
                  }px`,
                }}>
                {acceptedFiles}
                {existsFiles}
              </div>
            ) : (
              <div className='d-flex align-items-center justify-content-center h-100'>
                <p className='fs-5'>{textTypeFile}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
